import React, { useState, useEffect } from 'react';
import { getStoredAPIKey, storeAPIKey } from '../../lib/rag/essentialExports';

/**
 * Component for entering and managing HuggingFace API key
 */
export default function ApiKeyInput({ onApiKeySet, isOpen = true, onClose }) {
  const [apiKey, setApiKey] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  
  // Load any existing API key on mount
  useEffect(() => {
    const storedKey = getStoredAPIKey();
    if (storedKey) {
      setApiKey(storedKey);
      setSaved(true);
    }
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!apiKey.trim()) {
      setError("Please enter an API key");
      return;
    }
    
    try {
      // Store the API key
      storeAPIKey(apiKey);
      setSaved(true);
      setError(null);
      
      // Notify parent component
      if (onApiKeySet) {
        onApiKeySet(apiKey);
      }
    } catch (error) {
      setError(`Failed to save API key: ${error.message}`);
    }
  };
  
  const handleChange = (e) => {
    setApiKey(e.target.value);
    setSaved(false);
    setError(null);
  };
  
  // If not open, don't render anything
  if (!isOpen) return null;
  
  // Render as modal if onClose is provided
  if (onClose) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
          <h2 className="text-xl font-bold mb-4">Hugging Face API Key Required</h2>
          <p className="mb-4">
            RAWPA AI needs a Hugging Face API key to function. Your key is stored locally in your browser and is never sent to our servers.
          </p>
          <p className="mb-2 text-sm">
            The system uses the <strong>Qwen/Qwen3-235B-A22B</strong> model for advanced XSS payload generation and other security features.
          </p>
          <p className="mb-2 text-sm">
            To get a free Hugging Face API key:
          </p>
          <ol className="list-decimal ml-5 mb-4 text-sm">
            <li>Visit <a href="https://huggingface.co/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">huggingface.co/settings/tokens</a></li>
            <li>Sign up or log in</li>
            <li>Create a new token (Read access is sufficient)</li>
            <li>Copy and paste the token here</li>
          </ol>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <input
                type="password"
                value={apiKey}
                onChange={handleChange}
                placeholder="Enter Hugging Face API key"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
              
              {error && (
                <div className="mt-2 text-sm text-red-600">
                  {error}
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!apiKey.trim()}
                className="px-4 py-2 bg-[#daed6e] text-black rounded-md hover:bg-[#c5d85c] disabled:opacity-50"
              >
                Save API Key
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
  
  // Render as inline component
  return (
    <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 mb-4">
      <h3 className="text-lg font-medium mb-2">HuggingFace API Key</h3>
      
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="mb-2">
          <label htmlFor="api-key" className="block text-sm font-medium text-gray-700 mb-1">
            API Key
          </label>
          <input
            id="api-key"
            type="password"
            value={apiKey}
            onChange={handleChange}
            placeholder="Enter your HuggingFace API key"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        {error && (
          <div className="mb-2 text-sm text-red-600">
            {error}
          </div>
        )}
        
        {saved && !error && (
          <div className="mb-2 text-sm text-green-600">
            API key saved successfully
          </div>
        )}
        
        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Save API Key
        </button>
      </form>
      
      <div className="mt-3 text-xs text-gray-600">
        Get your HuggingFace API key at <a href="https://huggingface.co/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">huggingface.co/settings/tokens</a>
      </div>
    </div>
  );
} 